export const ALL = 'all';
export const FACEBOOK = 'facebook';
export const FACEBOOK_GROUP = 'facebook_group';
export const INSTAGRAM = 'instagram';
export const GOOGLE = 'google';
export const TWITTER = 'twitter';
export const LINKEDIN_PERSONAL_ACCOUNT = 'linkedin_personal_account';
export const LINKEDIN_BUSINESS_PAGE = 'linkedin_business_page';
export const TIKTOK_PERSONAL_ACCOUNT = 'tiktok_personal_account';
export const TIKTOK_BUSINESS_PAGE = 'tiktok_business_page';
export const YOUTUBE = 'youtube';
export const WHATSAPP = 'whatsapp';
export const PINTEREST = 'pinterest';
export const PINTEREST_AI_DISCLOSURE_AI_MODIFIED = 'AI_MODIFIED';
export const PINTEREST_AI_DISCLOSURE_SYNTHETIC_PERFORMER =
  'SYNTHETIC_PERFORMER';
export const GENERIC = 'generic';
export const VIDEO_PLACEHOLD = 'video_placeholder';
export const META = 'meta';

// Maximum length of a *comment / public reply* sent to a platform, per
// platform documentation. Keep in sync with the matching dict in
// location_management/lomavis_communication/constants.py on the Django side —
// the backend enforces these same limits as a hard validation gate.
export const PLATFORM_MAX_COMMENT_LENGTH: Partial<Record<string, number>> = {
  [TIKTOK_BUSINESS_PAGE]: 150,
  [TIKTOK_PERSONAL_ACCOUNT]: 150,
  [TWITTER]: 280,
  [LINKEDIN_BUSINESS_PAGE]: 1250,
  [INSTAGRAM]: 2200,
  [GOOGLE]: 4096,
  [FACEBOOK]: 8000,
  [YOUTUBE]: 10000
};

// Maximum length of a *direct message* sent to a platform.
export const PLATFORM_MAX_DIRECT_MESSAGE_LENGTH: Partial<
  Record<string, number>
> = {
  [INSTAGRAM]: 1000,
  [WHATSAPP]: 4096,
  [TWITTER]: 10000,
  [FACEBOOK]: 20000
};

export const CONVERSATION_TYPE_DIRECT_MESSAGE = 'direct_message';

/**
 * Returns the platform-imposed character limit for a message of the given
 * conversation type, or `undefined` if the platform has no known limit for
 * that conversation type.
 */
export const getPlatformMaxMessageLength = (
  platform: string | undefined | null,
  conversationType: string | undefined | null
): number | undefined => {
  if (!platform) return undefined;
  const limits =
    conversationType === CONVERSATION_TYPE_DIRECT_MESSAGE
      ? PLATFORM_MAX_DIRECT_MESSAGE_LENGTH
      : PLATFORM_MAX_COMMENT_LENGTH;
  return limits[platform];
};

export const PRIMARY = 'primary';
export const PRIMARY_DARK = 'primary_dark';

export const OVERVIEW_FOLLOWERS_KEY = 'dashboard.overview.followers';
export const OVERVIEW_FOLLOWER_KEY = 'dashboard.overview.follower';

export const UPLOAD_TYPE_ATTACHMENT = 1;
export const UPLOAD_TYPE_PREVIEW_IMAGE = 2;
export const UPLOAD_TYPE_MEDIA_LIBRARY = 3;
export const UPLOAD_TYPE_THUMBNAIL = 4;
export const UPLOAD_TYPE_POST_MEDIA = 5;
export const UPLOAD_TYPE_IDEA_MEDIA = 6;
export const UPLOAD_TYPE_OVERLAY_MEDIA = 7;

export const SELECTED_PLATFORM_COLOR_CODE = {
  [ALL]: '#4A90E2',
  [FACEBOOK]: '#3b5998',
  [INSTAGRAM]: '#e4405f',
  [GOOGLE]: '#4285f4',
  [TWITTER]: '#1da1f2',
  [LINKEDIN_PERSONAL_ACCOUNT]: '#0077b5',
  [LINKEDIN_BUSINESS_PAGE]: '#0077b5',
  [TIKTOK_PERSONAL_ACCOUNT]: '#000000',
  [TIKTOK_BUSINESS_PAGE]: '#000000',
  [YOUTUBE]: '#ff0000',
  [FACEBOOK_GROUP]: '#3b5998',
  [WHATSAPP]: '#25d366',
  [PINTEREST]: '#bd081c',
  [GENERIC]: '#7E8299'
};

export const MAIN_COLOR_CODES = {
  [PRIMARY]: '#3b5998',
  [PRIMARY_DARK]: '#28346C'
};

export const ANALYTICS_COLOR_LINKEDIN_BUSINESS_PAGE =
  SELECTED_PLATFORM_COLOR_CODE[LINKEDIN_BUSINESS_PAGE];
export const ANALYTICS_COLOR_FACEBOOK = SELECTED_PLATFORM_COLOR_CODE[FACEBOOK];
export const ANALYTICS_COLOR_FACEBOOK_GROUP =
  SELECTED_PLATFORM_COLOR_CODE[FACEBOOK_GROUP];

export const ANALYTICS_COLOR_TWITTER = SELECTED_PLATFORM_COLOR_CODE[TWITTER];
export const ANALYTICS_COLOR_LINKEDIN_PERSONAL_PROFILE =
  SELECTED_PLATFORM_COLOR_CODE[LINKEDIN_PERSONAL_ACCOUNT];
export const ANALYTICS_COLOR_INSTAGRAM =
  SELECTED_PLATFORM_COLOR_CODE[INSTAGRAM];
export const ANALYTICS_COLOR_GOOGLE = SELECTED_PLATFORM_COLOR_CODE[GOOGLE];
export const ANALYTICS_COLOR_POSTS = SELECTED_PLATFORM_COLOR_CODE[GENERIC];
export const ANALYTICS_COLOR_YOUTUBE = SELECTED_PLATFORM_COLOR_CODE[YOUTUBE];
export const ANALYTICS_COLOR_TIKTOK_BUSINESS_PROFILE =
  SELECTED_PLATFORM_COLOR_CODE[TIKTOK_BUSINESS_PAGE];
export const ANALYTICS_COLOR_TIKTOK_PERSONAL_PROFILE =
  SELECTED_PLATFORM_COLOR_CODE[TIKTOK_PERSONAL_ACCOUNT];
export const ANALYTICS_COLOR_PINTEREST =
  SELECTED_PLATFORM_COLOR_CODE[PINTEREST];

export const ANALYTICS_COLOR_YELLOW = ANALYTICS_COLOR_POSTS;

export const ANALYTICS_COLOR_1 = '#009ef7';
export const ANALYTICS_COLOR_2 = '#A1A5B7';
export const ANALYTICS_COLOR_3 = '#50cd89';
export const ANALYTICS_COLOR_4 = '#7239ea';
export const ANALYTICS_COLOR_5 = '#ffc700';
export const ANALYTICS_COLOR_6 = '#f1416c';
export const ANALYTICS_COLOR_7 = '#181C32';

export const ANALYTICS_COLOR_8 = '#E1E3EA';

export const WHATSAPP_TEMPLATE_BG_COLOR = '#e4dcd4';
export const WHATSAPP_TEMPLATE_UNDERLAY_COLOR = '#f6eef0';

export const IMPRESSIONS = 'impressions';
export const REACTIONS = 'reactions';
export const SHARES = 'shares';
export const COMMENTS = 'comments';
export const VIDEO_VIEWS = 'video_views';
export const REACH = 'reach';
export const ENGAGEMENT = 'engagement';
export const ENGAGEMENT_RATE = 'engagement_rate';

export const SHORT_ANALYTICS_METRICS_TITLES = [
  'impressions',
  'reach',
  'reactions',
  'comments',
  'shares',
  'engagement',
  'post_performance_rating'
];

export const PLATFORMS_COLORS = {
  [FACEBOOK]: ANALYTICS_COLOR_FACEBOOK,
  [FACEBOOK_GROUP]: ANALYTICS_COLOR_FACEBOOK_GROUP,
  [INSTAGRAM]: ANALYTICS_COLOR_INSTAGRAM,
  [GOOGLE]: ANALYTICS_COLOR_GOOGLE,
  [TWITTER]: ANALYTICS_COLOR_TWITTER,
  [LINKEDIN_PERSONAL_ACCOUNT]: ANALYTICS_COLOR_LINKEDIN_PERSONAL_PROFILE,
  [LINKEDIN_BUSINESS_PAGE]: ANALYTICS_COLOR_LINKEDIN_BUSINESS_PAGE
};

export const DETAILS_ANALYTICS_METRICS_ORDER = [
  REACH,
  IMPRESSIONS,
  REACTIONS,
  SHARES,
  COMMENTS
];

export const PLATFORM_LIST = [
  FACEBOOK,
  FACEBOOK_GROUP,
  INSTAGRAM,
  GOOGLE,
  TWITTER,
  LINKEDIN_PERSONAL_ACCOUNT,
  LINKEDIN_BUSINESS_PAGE,
  YOUTUBE,
  TIKTOK_PERSONAL_ACCOUNT,
  TIKTOK_BUSINESS_PAGE,
  WHATSAPP,
  PINTEREST,
  GENERIC
];

export const PLATFORM_ICON_CSS_CLASS = {
  [ALL]: 'fa-duotone fa-layer-group',
  [FACEBOOK]: 'fa-brands fa-facebook',
  [FACEBOOK_GROUP]: 'fa-brands fa-facebook',
  [INSTAGRAM]: 'fa-brands fa-instagram',
  [GOOGLE]: 'fa-kit fa-google',
  [TWITTER]: 'fa-brands fa-twitter',
  [LINKEDIN_PERSONAL_ACCOUNT]: 'fa-brands fa-linkedin',
  [LINKEDIN_BUSINESS_PAGE]: 'fa-brands fa-linkedin',
  [YOUTUBE]: 'fa-brands fa-youtube',
  [TIKTOK_PERSONAL_ACCOUNT]: 'fa-brands fa-tiktok',
  [TIKTOK_BUSINESS_PAGE]: 'fa-brands fa-tiktok',
  [WHATSAPP]: 'fa-brands fa-whatsapp',
  [PINTEREST]: 'fa-brands fa-pinterest',
  [GENERIC]: 'fa-duotone fa-globe'
};

export const PLATFORM_LIST_NAMES = {
  [FACEBOOK]: 'Facebook',
  [FACEBOOK_GROUP]: 'Facebook group',
  [INSTAGRAM]: 'Instagram',
  [GOOGLE]: 'Google',
  [TWITTER]: 'Twitter',
  [LINKEDIN_PERSONAL_ACCOUNT]: 'LinkedIn',
  [LINKEDIN_BUSINESS_PAGE]: 'LinkedIn Business',
  [YOUTUBE]: 'Youtube',
  [TIKTOK_PERSONAL_ACCOUNT]: 'Tiktok personal',
  [TIKTOK_BUSINESS_PAGE]: 'Tiktok business',
  [WHATSAPP]: 'Whatsapp',
  [PINTEREST]: 'Pinterest'
};

export const PLATFORM_LIST_SUPPORTED_IN_COCKPIT = [
  FACEBOOK,
  FACEBOOK_GROUP,
  INSTAGRAM,
  GOOGLE,
  TWITTER,
  LINKEDIN_PERSONAL_ACCOUNT,
  LINKEDIN_BUSINESS_PAGE,
  YOUTUBE,
  TIKTOK_BUSINESS_PAGE
];

// Supported platforms for demographics data
export const SUPPORTED_PLATFORMS_DEMOGRAPHICS = [
  FACEBOOK,
  INSTAGRAM,
  LINKEDIN_BUSINESS_PAGE
];

export const UPLOAD_TYPES = [
  UPLOAD_TYPE_ATTACHMENT,
  UPLOAD_TYPE_PREVIEW_IMAGE,
  UPLOAD_TYPE_MEDIA_LIBRARY,
  UPLOAD_TYPE_THUMBNAIL,
  UPLOAD_TYPE_POST_MEDIA
];

export const UPLOAD_TO_PLATFORM_STATUS_SUCCESS = 3;
export const UPLOAD_TO_PLATFORM_STATUS_FAILED = 4;

export const PLATFORM_LIST_WITH_ALL = [ALL, ...PLATFORM_LIST];
export const PLATFORM_LIST_NAMES_WITH_ALL = {
  [ALL]: 'All Platforms',
  ...PLATFORM_LIST_NAMES
};

export const COCKPIT = 'cockpit';
export const CLOUD = 'cloud';

export const PLATFORM_LIST_TOLGEE_NAMES = {
  [ALL]: 'platforms.all',
  [FACEBOOK]: `platforms.${FACEBOOK}`,
  [FACEBOOK_GROUP]: `platforms.${FACEBOOK_GROUP}`,
  [INSTAGRAM]: `platforms.${INSTAGRAM}`,
  [GOOGLE]: `platforms.${GOOGLE}`,
  [TWITTER]: `platforms.${TWITTER}`,
  [LINKEDIN_PERSONAL_ACCOUNT]: `platforms.${LINKEDIN_PERSONAL_ACCOUNT}`,
  [LINKEDIN_BUSINESS_PAGE]: `platforms.${LINKEDIN_BUSINESS_PAGE}`,
  [YOUTUBE]: `platforms.${YOUTUBE}`,
  [TIKTOK_PERSONAL_ACCOUNT]: `platforms.${TIKTOK_PERSONAL_ACCOUNT}`,
  [TIKTOK_BUSINESS_PAGE]: `platforms.${TIKTOK_BUSINESS_PAGE}`,
  [WHATSAPP]: `platforms.${WHATSAPP}`,
  [PINTEREST]: `platforms.${PINTEREST}`,
  [GENERIC]: `platforms.${GENERIC}`
};

export const UPGRADE_SUBSCRIPTION_URL = '/subscription-management/select/';
export const SOURCE_CHOICE_LOMAVIS_WEB_UI = 0;
export const SOURCE_CHOICE_LOMAVIS_MOBILE = 1;
export const SOURCE_CHOICE_LOMAVIS_API = 2;
export const SOURCE_CHOICE_BULK_UPLOAD = 3;

// Configuration for which metrics are NOT available for each platform (negative list)
export const PLATFORM_METRICS_AVAILABILITY = {
  [YOUTUBE]: {
    [REACH]: false,
    [ENGAGEMENT]: false
  }
  // Add other platforms as needed with their unavailable metrics
};

/**
 * Ad Manager — media upload & validation constants.
 * Centralized so the ad manager and (later) lomavis creator share the same
 * numbers when they enforce Meta's advertising rules.
 */
export const AD_MANAGER_MEDIA_UPLOAD_MAX_FILES = 10;
export const AD_MANAGER_MEDIA_UPLOAD_MAX_VIDEOS = 1;
export const AD_MANAGER_MAX_IMAGE_SIZE_BYTES = 30 * 1024 * 1024;
export const AD_MANAGER_MAX_VIDEO_SIZE_BYTES = 1 * 1024 * 1024 * 1024;
export const AD_MANAGER_MIN_SHORTEST_SIDE_PX = 600;
export const AD_MANAGER_RECOMMENDED_SHORTEST_SIDE_PX = 1080;
export const AD_MANAGER_ASPECT_RATIO_TOLERANCE = 0.01;
export const AD_MANAGER_MEDIA_PLACEMENT_RATIO_RANGES = {
  // Feed: 4:5 (portrait, 0.8) to 1.91:1 (landscape, 1.91)
  feed: { min: 0.8, max: 1.91 },
  // Stories/Reels: 9:16 (0.5625) to 1.91:1 tolerance
  stories: { min: 0.5625, max: 1.91 }
} as const;

/**
 * Matches the first url in a block of post text. Lives here because the
 * editor highlights and shortens urls with it while the link preview looks
 * them up — a leaf constant all three can share without pulling each other in.
 *
 * Moved verbatim from the editor's config, escapes and all: the redundant
 * ones trip `no-useless-escape`, but a url regex is load-bearing and a move
 * is the wrong moment to reword it.
 */
/* eslint-disable no-useless-escape */
export const URL_REGEX =
  /(^| {1})((?:(?:(?:http|https):(?:\/\/)?)(?:[\-;:&=\+\$,\w]+)?[A-Za-z0-9\.\-]+\.\w{2,}|(?:www\.)[A-Za-z0-9\.\-]+\.\w{2,})(?:\/{0,1}[\+~%\/\.\w\-_]*)?\??(?:[\-\+=&;%@\.\w_\*\[\]]*)#?(?:[\.\!\/\\\w\-]*)?)/gim;
/* eslint-enable no-useless-escape */
